import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Autocomplete,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon, Print as PrintIcon } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import config from '../config';
import { StockLevel } from '../types/stockLevels.types';
import { stockLevelsService } from '../services/stockLevels.service';
import { warehousesService } from '../services/warehouses.service';
import { branchesService } from '../services/branches.service';
import { Warehouse } from '../types/warehouses.types';
import { Branch } from '../types/branches.types';

const translations = {
  en: {
    title: 'Stock Levels',
    itemCode: 'Item Code',
    itemName: 'Item Name',
    itemNameAr: 'Item Name (AR)',
    unit: 'Unit',
    location: 'Location',
    warehouse: 'Warehouse',
    branch: 'Branch',
    quantity: 'Quantity',
    reserved: 'Reserved',
    available: 'Available',
    lastUpdated: 'Last Updated',
    search: 'Search by item code or name...',
    noData: 'No stock data found',
    filterByWarehouse: 'Filter by Warehouse',
    filterByBranch: 'Filter by Branch',
    all: 'All',
  },
  ar: {
    title: 'أرصدة المخزون',
    itemCode: 'رمز الصنف',
    itemName: 'اسم الصنف',
    itemNameAr: 'اسم الصنف (عربي)',
    unit: 'الوحدة',
    location: 'الموقع',
    warehouse: 'المخزن',
    branch: 'الفرع',
    quantity: 'الكمية',
    reserved: 'المحجوز',
    available: 'المتاح',
    lastUpdated: 'آخر تحديث',
    search: 'البحث برمز أو اسم الصنف...',
    noData: 'لا توجد بيانات مخزون',
    filterByWarehouse: 'تصفية حسب المخزن',
    filterByBranch: 'تصفية حسب الفرع',
    all: 'الكل',
  },
};

const StockLevelsPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const { userBranchIds, userWarehouseIds } = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return { userBranchIds: [] as number[], userWarehouseIds: [] as number[] };
      const payload = JSON.parse(atob(token.split('.')[1]));
      const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
      const allowedBranches = payload.AllowedBranchId
        ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
        : [];
      const branchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
      const keeperClaims = payload.KeeperWarehouseId;
      const warehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
      return { userBranchIds: branchIds, userWarehouseIds: warehouseIds };
    } catch {
      return { userBranchIds: [] as number[], userWarehouseIds: [] as number[] };
    }
  })();

  const canViewStock = (sl: StockLevel) => {
    if (userBranchIds.length === 0 && userWarehouseIds.length === 0) return true;
    if (sl.warehouseId && userWarehouseIds.includes(sl.warehouseId)) return true;
    if (sl.branchId && userBranchIds.includes(sl.branchId)) return true;
    return false;
  };

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
  }, []);

  const [stockLevels, setStockLevels] = useState<StockLevel[]>([]);
  const [filteredStockLevels, setFilteredStockLevels] = useState<StockLevel[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterStockLevels();
  }, [searchTerm, stockLevels, selectedWarehouse, selectedBranch]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [stockData, warehousesData, branchesData] = await Promise.all([
        stockLevelsService.getAll(),
        warehousesService.getAll(),
        branchesService.getAll(),
      ]);

      const filteredStock = stockData.filter((sl: StockLevel) => canViewStock(sl));
      setStockLevels(filteredStock);

      const filteredWarehouses = userWarehouseIds.length === 0 && userBranchIds.length === 0
        ? warehousesData
        : warehousesData.filter((w: Warehouse) => 
            userWarehouseIds.includes(w.warehouseId) || 
            (w.branchId && userBranchIds.includes(w.branchId))
          );
      setWarehouses(filteredWarehouses);

      const filteredBranches = userBranchIds.length === 0 && userWarehouseIds.length === 0
        ? branchesData
        : branchesData.filter((b: Branch) => userBranchIds.includes(b.branchId));
      setBranches(filteredBranches);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterStockLevels = () => {
    let filtered = [...stockLevels];

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (sl) =>
          sl.itemCode.toLowerCase().includes(term) ||
          sl.itemName.toLowerCase().includes(term) ||
          sl.itemNameAr.toLowerCase().includes(term)
      );
    }

    // Filter by warehouse
    if (selectedWarehouse) {
      filtered = filtered.filter((sl) => sl.warehouseId === selectedWarehouse.warehouseId);
    }

    // Filter by branch
    if (selectedBranch) {
      filtered = filtered.filter((sl) => sl.branchId === selectedBranch.branchId);
    }

    setFilteredStockLevels(filtered);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-KW' : 'en-GB');
  };

  const getLocationDisplay = (sl: StockLevel) => {
    if (sl.warehouseId) {
      return isRTL ? sl.warehouseNameAr : sl.warehouseName;
    }
    if (sl.branchId) {
      return isRTL ? sl.branchNameAr : sl.branchName;
    }
    return '-';
  };

  const getLocationType = (sl: StockLevel) => {
    if (sl.warehouseId) return t.warehouse;
    if (sl.branchId) return t.branch;
    return '-';
  };

  const handlePrintList = () => {
    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:#1976d2;margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const rows = filteredStockLevels.map(sl => `
      <tr>
        <td>${sl.itemCode}</td>
        <td>${isRTL ? sl.itemNameAr : sl.itemName}</td>
        <td>${isRTL ? sl.unitNameAr : sl.unitName}</td>
        <td>${getLocationDisplay(sl)}</td>
        <td style="text-align:right">${sl.quantity?.toFixed(2) || '0.00'}</td>
        <td style="text-align:right">${sl.reservedQuantity?.toFixed(2) || '0.00'}</td>
        <td style="text-align:right">${sl.availableQuantity?.toFixed(2) || '0.00'}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head>
      <style>
        body{font-family:${isRTL ? 'Cairo,' : ''}Arial,sans-serif;margin:20px;font-size:12px;color:#333;}
        .header{text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;}
        .header h2{color:#1976d2;margin:5px 0;}
        .header p{font-size:11px;color:#666;margin:0;}
        h1{text-align:center;color:#1976d2;margin:10px 0 15px;font-size:18px;}
        table{width:100%;border-collapse:collapse;margin-top:10px;}
        th,td{border:1px solid #ddd;padding:6px 8px;font-size:11px;}
        th{background:#1976d2;color:white;font-weight:bold;}
        tr:nth-child(even){background:#f9f9f9;}
        @page{size:A4 landscape;margin:1cm;}
      </style></head><body>
      ${companyHeader}
      <h1>${t.title}</h1>
      <table>
        <thead><tr>
          <th>${t.itemCode}</th>
          <th>${t.itemName}</th>
          <th>${t.unit}</th>
          <th>${t.location}</th>
          <th>${t.quantity}</th>
          <th>${t.reserved}</th>
          <th>${t.available}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="margin-top:10px;font-size:11px;color:#999;">Printed: ${new Date().toLocaleString()}</p>
    </body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 500); }
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">
          {t.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={isRTL ? 'طباعة القائمة' : 'Print List'}>
            <IconButton onClick={handlePrintList} color="primary"><PrintIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={loadData} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          <TextField
            fullWidth
            placeholder={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Autocomplete
            options={warehouses}
            getOptionLabel={(option) => isRTL ? option.warehouseNameAr : option.warehouseName}
            value={selectedWarehouse}
            onChange={(_, newValue) => setSelectedWarehouse(newValue)}
            renderInput={(params) => (
              <TextField {...params} label={t.filterByWarehouse} />
            )}
          />

          <Autocomplete
            options={branches}
            getOptionLabel={(option) => isRTL ? option.branchNameAr : option.branchName}
            value={selectedBranch}
            onChange={(_, newValue) => setSelectedBranch(newValue)}
            renderInput={(params) => (
              <TextField {...params} label={t.filterByBranch} />
            )}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t.itemCode}</TableCell>
                <TableCell>{t.itemName}</TableCell>
                <TableCell>{t.unit}</TableCell>
                <TableCell>{t.location}</TableCell>
                <TableCell align="right">{t.quantity}</TableCell>
                <TableCell align="right">{t.reserved}</TableCell>
                <TableCell align="right">{t.available}</TableCell>
                <TableCell>{t.lastUpdated}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStockLevels.map((sl) => (
                <TableRow key={sl.stockLevelId}>
                  <TableCell>{sl.itemCode}</TableCell>
                  <TableCell>{isRTL ? sl.itemNameAr : sl.itemName}</TableCell>
                  <TableCell>
                    <Chip
                      label={isRTL ? sl.unitNameAr : sl.unitName}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {getLocationDisplay(sl)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getLocationType(sl)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {sl.quantity?.toFixed(2) || '0.00'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography color="warning.main">
                      {sl.reservedQuantity?.toFixed(2) || '0.00'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography color="success.main" fontWeight="bold">
                      {sl.availableQuantity?.toFixed(2) || '0.00'}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(sl.lastUpdated)}</TableCell>
                </TableRow>
              ))}
              {filteredStockLevels.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {t.noData}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default StockLevelsPage;